import { useMemo } from "react";

import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getDashboardMetrics } from "@/service/reqUrl.service";

import type { DashboardMetricsData, DashboardMetricsParams } from "./types";

interface UseDashboardMetricsResult {
  /** Formatted (`categories`) or the SP's raw payload — normalize with
   *  `toMetricCategories` before use. */
  data: DashboardMetricsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Metric values for one dashboard (metric category), via
 * POST dashboard/dashboard-metric. Re-fires whenever the params change — i.e.
 * when the user switches dashboard, service line, or date range.
 *
 * `params` is null until the caller has a dashboard id to ask for (e.g. the
 * Current State pillar has no dashboard); the hook stays idle in that case.
 *
 * ── On the old `doneKey` guard ─────────────────────────────────────────────
 * This hook used to track which params the data in state belonged to, so that a
 * dashboard switch read as loading from the very first render and the previous
 * dashboard's values never flashed through. That guard is no longer needed: the
 * params ARE the query key, so a switch moves to a different cache entry whose
 * data is `undefined` until it resolves — the same result, for free.
 *
 * For the same reason `placeholderData: keepPreviousData` must NOT be added
 * here. It would re-introduce exactly the stale flash `doneKey` existed to
 * prevent.
 */
export function useDashboardMetrics(
  params: DashboardMetricsParams | null,
): UseDashboardMetricsResult {
  const userKey = useUserKey();
  // The save flag rides in the body only — the same filters are the same data
  // whether or not the SP records them, so they share one cache entry.
  const keyParams = useMemo(() => {
    if (!params) return null;
    const { saveUserFilterPreferences, ...rest } = params;
    return rest;
  }, [params]);

  const { data, loading, error, refetch } = useApiQuery<DashboardMetricsData>({
    queryKey: qk.metrics(userKey, keyParams),
    url: getDashboardMetrics,
    body: params,
    enabled: params != null,
    fallbackMessage: "Failed to fetch dashboard metrics",
    // One of only two hooks that toasted on an HTTP failure before the
    // migration. A dashboard with no enabled metrics is an empty state, not a
    // failure — `apiErrorInfo` flags it `toast: false`, so it still gets the
    // inline message and no toast.
    meta: { toastOnError: true },
  });

  return { data, loading, error, refetch };
}
